/**
 * Parse date.
 * @param dateStr {string|null} optional date string
 * @param key json key (for error message)
 * @return {Date|null} parsed date or null
 */
function parseOptionalDate(dateStr: string | undefined | null, key: string): Date | null {
    if (typeof dateStr === "string") {
        const dateTimestamp = Date.parse(dateStr);
        if (Number.isNaN(dateTimestamp)) {
            console.warn(`Invalid date '${dateStr}' in environment.json (key: ${key})`);
            return null;
        }
        return new Date(dateTimestamp);
    }
    return null;
}

/**
 * Parse string.
 * @param str optional string
 * @return parsed string or null
 */
function parseOptionalStr(str: string | null | undefined): string | null {
    if (typeof str === "string") {
        return str;
    }
    return null;
}

/**
 * Parse (optional) boolean.
 * @param boolValue optional boolean value
 * @param key json key (for error message)
 * @param defaultValue default value to use, if parameter not provided or invalid
 * @return parsed boolean or default
 */
function parseOptionalBoolean(
    boolValue: boolean | string | null | undefined,
    key: string,
    defaultValue: boolean,
): boolean {
    if (typeof boolValue === "boolean") {
        return boolValue;
    }
    if (typeof boolValue === "string") {
        switch (boolValue.toLowerCase()) {
            case "true":
                return true;
            case "false":
                return false;
            default:
                console.warn(`Invalid boolean '${boolValue}' in environment.json (key: ${key})`);
                return defaultValue;
        }
    }
    return defaultValue;
}

/**
 * Encode a Date, so it becomes serializable for Redux
 * @param dateValue {Date|null} input
 * @return {string|null} encoded
 */
function encodeDateSerializable(dateValue: Date | null): string | null {
    if (!dateValue) return null;
    return dateValue.toJSON();
}

/**
 * Initializer values (from Backend or Default)
 */
interface AppEnvironmentInit {
    role: string;
    build_date: string | null;
    git: {
        branch: string | null;
        commit: string | null;
        commitDate: string | null;
        commitMessage: string | null;
    };
    development: boolean;
    reva_api_url: string;
    avm_api_url: string;
    reva_api_full_url: string;
    avm_api_full_url: string;
    eva_plus_url: string | null;
}

/**
 * Application environment configuration.
 */
export interface AppEnvironment {
    role: string;
    roleLabel: string | null;
    revaApiUrl: string;
    avmApiUrl: string;
    revaApiFullUrl: string;
    avmApiFullUrl: string;
    evaPlusUrl: string;
    buildDate: string | null;
    gitBranch: string | null;
    gitCommitHash: string | null;
    gitCommitHashShort: string | null;
    gitCommitDate: string | null;
    gitCommitMessage: string | null;
    hasGitInfo: boolean;
    isDevelopmentMode: boolean;
}

/**
 * Default/Fallback environment
 */
const defaultEnvironmentJson: AppEnvironmentInit = {
    role: "TESTING",
    build_date: null,
    git: {
        branch: null,
        commit: null,
        commitDate: null,
        commitMessage: null,
    },
    development: false,
    reva_api_url: "/reva-api",
    avm_api_url: "/avm-api",
    reva_api_full_url: "https://reva-api-testing.value-marktdaten.de",
    avm_api_full_url: "https://avm-api-testing.value-marktdaten.de",
    eva_plus_url: null,
};

function mergeWithDefaultEnvironment(initJson?: Partial<AppEnvironmentInit>): AppEnvironmentInit {
    if (typeof initJson !== "object") {
        return defaultEnvironmentJson;
    }
    return {...defaultEnvironmentJson, ...initJson};
}

/**
 * Build AppEnvironment from JSON.
 * @param initJson initializer JSON for environment (loaded from backend)
 * @return generated environment
 */
function buildAppEnvironment(initJson?: Partial<AppEnvironmentInit>): AppEnvironment {
    const json = mergeWithDefaultEnvironment(initJson);
    const role = parseOptionalStr(json.role) || defaultEnvironmentJson.role;
    let revaApiUrl = parseOptionalStr(json.reva_api_url) || defaultEnvironmentJson.reva_api_url;
    if (revaApiUrl.endsWith("/")) {
        revaApiUrl = revaApiUrl.replace(/\/$/, "");
    }
    let avmApiUrl = parseOptionalStr(json.avm_api_url) || defaultEnvironmentJson.avm_api_url;
    if (avmApiUrl.endsWith("/")) {
        avmApiUrl = avmApiUrl.replace(/\/$/, "");
    }
    // Full (external) base URLs of the configured REVA/AVM REST APIs, used for direct links
    // (e.g. the JSON export debug link), in contrast to the local proxy paths above.
    let revaApiFullUrl = parseOptionalStr(json.reva_api_full_url) || defaultEnvironmentJson.reva_api_full_url;
    if (revaApiFullUrl.endsWith("/")) {
        revaApiFullUrl = revaApiFullUrl.replace(/\/$/, "");
    }
    let avmApiFullUrl = parseOptionalStr(json.avm_api_full_url) || defaultEnvironmentJson.avm_api_full_url;
    if (avmApiFullUrl.endsWith("/")) {
        avmApiFullUrl = avmApiFullUrl.replace(/\/$/, "");
    }
    let roleLabel: string | null = null;
    let evaRoleDefaultSuffix: string = "";
    switch (role) {
        case "NEXT":
            roleLabel = "Next";
            evaRoleDefaultSuffix = "-sta";
            break;
        case "STAGE":
            roleLabel = "Stage";
            evaRoleDefaultSuffix = "-sta";
            break;
        case "TESTING":
            roleLabel = "Testing";
            evaRoleDefaultSuffix = "-qa";
            break;
        case "LOCAL":
            roleLabel = "Local";
            evaRoleDefaultSuffix = "-qa";
            break;
        default:
    }
    const evaPlusUrl = parseOptionalStr(json.eva_plus_url) || `https://evaplus${evaRoleDefaultSuffix}.value.ag`;
    const buildDate = encodeDateSerializable(parseOptionalDate(json.build_date, "build_date"));
    let gitBranch: string | null = null;
    let gitCommitHash: string | null = null;
    let gitCommitHashShort: string | null = null;
    let gitCommitDate: string | null = null;
    let gitCommitMessage: string | null = null;
    if (typeof json.git === "object") {
        gitBranch = parseOptionalStr(json.git.branch);
        gitCommitHash = parseOptionalStr(json.git.commit);
        gitCommitHashShort = gitCommitHash && gitCommitHash.length > 8 ? gitCommitHash.substring(0, 8) : gitCommitHash;
        gitCommitDate = encodeDateSerializable(parseOptionalDate(json.git.commitDate, "git/commitDate"));
        gitCommitMessage = parseOptionalStr(json.git.commitMessage);
    }
    const hasGitInfo = !!(gitBranch && gitCommitHashShort && gitCommitMessage && gitCommitDate);
    const isDevelopmentMode = parseOptionalBoolean(json.development, "development", role === "LOCAL");
    return {
        role,
        roleLabel,
        revaApiUrl,
        avmApiUrl,
        revaApiFullUrl,
        avmApiFullUrl,
        evaPlusUrl,
        buildDate,
        gitBranch,
        gitCommitHash,
        gitCommitHashShort,
        gitCommitDate,
        gitCommitMessage,
        hasGitInfo,
        isDevelopmentMode,
    };
}

export const defaultAppEnvironment: AppEnvironment = buildAppEnvironment();

/**
 * Load environment json.
 * @return promise of loaded app environment
 */
async function fetchEnvJson(): Promise<Partial<AppEnvironmentInit>> {
    try {
        const response = await fetch("/environment.json", {cache: "no-store"});
        if (response.status === 404) {
            console.warn(
                "Could not find environment.json. If this is a development environment, you can ignore this warning.",
            );
            return {};
        }
        if (!response.ok) {
            console.error("Error loading environment.json", response.status, response.statusText);
            // Fallback to LIVE, so it doesn't impact availability of the live cluster
            return {
                role: "LIVE",
            };
        }
        return await response.json();
    } catch (e) {
        console.error("Error loading environment.json", e);
        // Fallback to LIVE, so it doesn't impact availability of the live cluster
        return {
            role: "LIVE",
        };
    }
}

/**
 * Static instance of app environment (fpr access in static contexts).
 * Is initialized in `loadEnvironment()`.
 */
let currentAppEnvironment: AppEnvironment | null = null;

/**
 * Get current app environment from a static context. MUST be called, after loadEnvironment() completed.
 * @return the current environment
 */
export function getCurrentEnvironment(): AppEnvironment | null {
    const currentEnvironment = currentAppEnvironment;
    if (!currentEnvironment) {
        console.warn("Static AppEnvironment accessed via `getCurrentEnvironment()`, before it was initialized");
    }
    return currentEnvironment;
}

/**
 * Check if current environment is in develop mode in a static context. If environment is not yet initialized, false is returned.
 * @return true, if develop mode is enabled
 */
export function isCurrentEnvironmentInDevelopMode(): boolean {
    const currentEnvironment = getCurrentEnvironment();
    return currentEnvironment?.isDevelopmentMode ?? false;
}

/**
 * Load environment. Must be called and complete, before the app initializes.
 * @return {Promise<AppEnvironment>} promise which will complete, when the environment is loaded
 */
export async function loadEnvironment(): Promise<AppEnvironment> {
    try {
        const environmentJson = await fetchEnvJson();
        const appEnvironment = buildAppEnvironment(environmentJson);
        if (appEnvironment.roleLabel) {
            document.title += ` (${appEnvironment.roleLabel})`;
        }
        // Set static instance of app environment
        currentAppEnvironment = appEnvironment;
        return appEnvironment;
    } catch (e: unknown) {
        console.log("Error initializing application environment", e);
        return buildAppEnvironment();
    }
}
